import { useEffect, useState, RefObject } from 'react'

export const useIsSearchFocused = (
  inputEl: RefObject<HTMLInputElement>,
  resultsEl: RefObject<HTMLUListElement>,
) => {
  const [isFocused, setIsFocused] = useState(false)

  // COMPOSED FOCUS STATE
  // When the input is focused, the dropdown should be visible
  // When the dropdown is focused, the dropdown should be visible
  // When focus moves from one to the other the dropdown should remain visible
  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      if (inputEl.current && inputEl.current.contains(event.target as Node)) {
        setIsFocused(true)
      }
    }

    const handleFocusOut = (event: FocusEvent) => {
      if (
        inputEl.current &&
        resultsEl.current &&
        !(
          inputEl.current.contains(event.relatedTarget as Node) ||
          resultsEl.current.contains(event.relatedTarget as Node)
        )
      ) {
        setIsFocused(false)
      }
    }

    if (inputEl.current && resultsEl.current) {
      inputEl.current.addEventListener('focusin', handleFocusIn)
      inputEl.current.addEventListener('focusout', handleFocusOut)
      resultsEl.current.addEventListener('focusout', handleFocusOut)
    }

    return () => {
      if (inputEl.current && resultsEl.current) {
        inputEl.current.removeEventListener('focusin', handleFocusIn)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        inputEl.current.removeEventListener('focusout', handleFocusOut)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        resultsEl.current.removeEventListener('focusout', handleFocusOut)
      }
    }
  }, [resultsEl, inputEl])

  // MOBILE KEYBOARD BEHAVIOR
  // Moves focus from input to dropdown on mobile
  // to trigger the software keyboard hiding so there is more space to view results
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

  useEffect(() => {
    const hideKeyboardOnDropdownInteraction = () => {
      if (isMobile && inputEl.current && resultsEl.current) {
        if (document.activeElement === inputEl.current) {
          resultsEl.current.focus()
        }
      }
    }

    const dropdown = resultsEl.current
    if (dropdown) {
      dropdown.addEventListener('touchstart', hideKeyboardOnDropdownInteraction)
    }

    return () => {
      if (dropdown) {
        dropdown.removeEventListener(
          'touchstart',
          hideKeyboardOnDropdownInteraction,
        )
      }
    }
  }, [inputEl, isMobile, resultsEl])

  return isFocused
}
